import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import Select from "react-select";
import {
  Container,
  Row,
  Col,
  Button,
  Form,
  Offcanvas,
  ListGroup,
  Card,
  Dropdown,
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import "../../styles/WorkHistoryPage.css";
import "../../styles/Form.css";
import SignatureManager from "../../components/SignatureManager";
import ReviewManager from "../../components/ReviewManager";
import CompanyAddress from "../../components/CompanyAddress";
import UploadedAttachmentList from "../../components/UploadedAttachmentList";
import logo from "../../images/logo.png";

const EMPLOYMENT_HISTORY_TASK_KEY = "employment_history";
const allowedAttachmentTypes = ["application/pdf", "image/jpeg", "image/png"];

function WorkHistoryPage() {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const { id: paramId } = useParams();
  const navigate = useNavigate();
  const role = localStorage.getItem("userRole");
  const isAdmin = role === "1";
  const token = localStorage.getItem("token");
  const authHeader = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
    }),
    [token],
  );
  const [isSigned, setIsSigned] = useState(false);
  const userId = isAdmin && paramId ? paramId : localStorage.getItem("userId");

  const [records, setRecords] = useState([]);
  const [referenceStatuses, setReferenceStatuses] = useState({});
  const [showDrawer, setShowDrawer] = useState(false);
  const [editRec, setEditRec] = useState(null);
  const [stillWorking, setStillWorking] = useState(false);
  const [stillSchool, setstillSchool] = useState(false);
  const [stillUnemployed, setStillUnemployed] = useState(false);
  const [stillDrivingSchool, setStillDrivingSchool] = useState(false);
  const [stillMilitary, setStillMilitary] = useState(false);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [isSavingAttachments, setIsSavingAttachments] = useState(false);
  const [isDeletingAttachmentId, setIsDeletingAttachmentId] = useState(null);
  const [eduOptions, setEduOptions] = useState([]);
  const [equipOptions, setEquipOptions] = useState([]);

  const isSignedConsentStatus = useCallback(
    (status) =>
      ["review", "completed", "almost_expired"].includes(
        String(status || "").toLowerCase(),
      ),
    [],
  );

  const [form, setForm] = useState({
    wh_type: "Working",
    wh_name: "",
    wh_confname: "",
    wh_conlname: "",
    wh_contact_position: "",
    wh_address1: "",
    wh_address2: "",
    wh_country: "",
    wh_postcode: "",
    wh_city: "",
    wh_province: "",
    wh_phone: "",
    wh_email: "",
    wh_datefrom: dayjs().format("YYYY-MM-DD"),
    wh_dateto: dayjs().format("YYYY-MM-DD"),
    wh_reason: "",
    wh_position: "",
    wh_status: "No",
    wh_terminated: "",
    wh_comm_vehicle: "",
    wh_comm_vehicle_types: [],
  });

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/work-history/${userId}`);
      setRecords(res.data);
    } catch (err) {
      console.error("Fetch failed", err);
    }
  }, [BASE_URL, userId]);

  const fetchSignedStatus = useCallback(async () => {
    try {
      const res = await axios.get(
        `${BASE_URL}/api/usertasks/status/${userId}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        },
      );

      const TASK_ID = 19; // employment_history
      const idKey = String(TASK_ID);
      const nameKey = "employment_history";

      const status =
        res.data.status?.[idKey] ??
        res.data.status?.[nameKey] ??
        "not_completed";

      setIsSigned(status === "completed");
    } catch (err) {
      console.error("Error fetching sign status", err);
    }
  }, [BASE_URL, userId]);

  const fetchReferenceStatuses = useCallback(async () => {
    try {
      const res = await axios.get(
        `${BASE_URL}/api/reference-check/statuses/${userId}`,
        {
          headers: authHeader,
        },
      );

      const statusMap = (res.data || []).reduce((acc, row) => {
        acc[row.wh_id] = row;
        return acc;
      }, {});
      setReferenceStatuses(statusMap);
    } catch (err) {
      console.error("Error fetching reference statuses", err);
    }
  }, [BASE_URL, authHeader, userId]);

  const fetchAttachments = useCallback(async () => {
    try {
      const res = await axios.get(
        `${BASE_URL}/api/custom-tasks/attachments/${encodeURIComponent(
          EMPLOYMENT_HISTORY_TASK_KEY,
        )}/${userId}`,
        {
          headers: authHeader,
        },
      );
      setExistingAttachments(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load work history attachments", err);
      setExistingAttachments([]);
    }
  }, [BASE_URL, authHeader, userId]);

  const fetchEduOptions = useCallback(async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/dropdowns/highest-edu`, {
        headers: authHeader,
      });
      setEduOptions(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load highest education options", err);
      setEduOptions([]);
    }
  }, [BASE_URL, authHeader]);

  const fetchEquipOptions = useCallback(async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/dropdowns/equip-operated`, {
        headers: authHeader,
      });
      setEquipOptions(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load equipment options", err);
      setEquipOptions([]);
    }
  }, [BASE_URL, authHeader]);

  useEffect(() => {
    fetchData();
    fetchSignedStatus();
    fetchReferenceStatuses();
    fetchAttachments();
    fetchEduOptions();
  }, [
    fetchAttachments,
    fetchData,
    fetchEduOptions,
    fetchEquipOptions,
    fetchReferenceStatuses,
    fetchSignedStatus,
  ]);

  useEffect(() => {
    if (form.wh_comm_vehicle === "Yes") {
      fetchEquipOptions();
      return;
    }

    if ((form.wh_comm_vehicle_types || []).length) {
      setForm((prev) => ({
        ...prev,
        wh_comm_vehicle_types: [],
      }));
    }
  }, [fetchEquipOptions, form.wh_comm_vehicle, form.wh_comm_vehicle_types]);

  const getReferenceMeta = (rec) => {
    const status = referenceStatuses[rec.wh_id];
    const hasEmail = Boolean((rec.wh_email || "").trim());
    const consentSigned = isSignedConsentStatus(status?.consent_status);
    const isExpired =
      status?.rc_expires_at && dayjs(status.rc_expires_at).isBefore(dayjs());
    const canSend =
      rec.wh_type === "Working" &&
      hasEmail &&
      (!status || status.rc_status !== "pending" || isExpired);

    let statusText = "Not sent";
    if (!hasEmail && rec.wh_type === "Working")
      statusText = "Missing company email";
    else if (!consentSigned)
      statusText = "Consent not signed yet";
    else if (status?.rc_status === "pending" && !isExpired)
      statusText = `Waiting for response until ${dayjs(
        status.rc_expires_at,
      ).format("YYYY-MM-DD")}`;
    else if (status?.rc_status === "completed" && !isExpired)
      statusText = `Completed on ${dayjs(status.rc_completed_at).format("YYYY-MM-DD")}`;
    else if (isExpired || status?.rc_status === "expired")
      statusText = `Expired on ${dayjs(status.rc_expires_at).format("YYYY-MM-DD")}`;

    return {
      status,
      hasEmail,
      consentSigned,
      canSend,
      canView: status?.rc_status === "completed" && Boolean(status?.rc_id),
      statusText,
      consentMessage: rec.wh_name
        ? `The user must sign "Review and Sign Consent - Consent to Previous Employment" for ${rec.wh_name} before sending the reference check.`
        : 'The user must sign "Review and Sign Consent - Consent to Previous Employment" before sending the reference check.',
    };
  };

  const handleSendReferenceCheck = async (rec) => {
    const meta = getReferenceMeta(rec);

    if (!meta.hasEmail) {
      alert("Company email is required before sending a reference check.");
      return;
    }

    if (!meta.consentSigned) {
      alert(meta.consentMessage);
      return;
    }

    if (!meta.canSend) {
      alert(meta.statusText);
      return;
    }

    if (
      !window.confirm(
        `Send a reference check email to ${rec.wh_email || "this company email"}?`,
      )
    ) {
      return;
    }

    try {
      await axios.post(
        `${BASE_URL}/api/reference-check/send/${rec.wh_id}`,
        {},
        { headers: authHeader },
      );
      alert("Reference check email sent successfully.");
      fetchReferenceStatuses();
    } catch (err) {
      console.error("Reference send failed", err);
      alert(
        err.response?.data?.message || "Failed to send reference check email.",
      );
    }
  };

  const handleCancelReferenceCheck = async (checkId) => {
    if (
      !window.confirm("Cancel this reference check so it can be sent again?")
    ) {
      return;
    }

    try {
      await axios.delete(`${BASE_URL}/api/reference-check/cancel/${checkId}`, {
        headers: authHeader,
      });
      alert("Reference check cancelled.");
      fetchReferenceStatuses();
    } catch (err) {
      console.error("Reference cancel failed", err);
      alert(err.response?.data?.message || "Failed to cancel reference check.");
    }
  };

  const openDrawer = (rec) => {
    if (rec) {
      // Editing an existing record
      setEditRec(rec);
      // Normalize wh_skills into an array of labels if Driving School
      let skillsArray = [];
      if (rec.wh_type === "Driving School" && rec.wh_skills) {
        try {
          const parsed =
            typeof rec.wh_skills === "string"
              ? JSON.parse(rec.wh_skills)
              : rec.wh_skills;
          if (parsed && typeof parsed === "object") {
            skillsArray = Object.entries(parsed)
              .filter(([_, val]) => val)
              .map(([key]) => {
                switch (key) {
                  case "border_crossing":
                    return "Border Crossing";
                  case "log_books":
                    return "Log Books";
                  case "fmcr":
                    return "Federal Motor Carrier Regulations";
                  case "hazmat":
                    return "Hazardous Materials";
                  default:
                    return null;
                }
              })
              .filter(Boolean);
          }
        } catch {
          skillsArray = [];
        }
      }

      setForm({
        ...rec,
        wh_type: rec.wh_type || "Working",
        wh_datefrom: dayjs(rec.wh_datefrom).format("YYYY-MM-DD"),
        wh_dateto: rec.wh_dateto
          ? dayjs(rec.wh_dateto).format("YYYY-MM-DD")
          : dayjs().format("YYYY-MM-DD"),
        wh_comm_vehicle_types: Array.isArray(rec.wh_comm_vehicle_types)
          ? rec.wh_comm_vehicle_types
          : (() => {
              try {
                const parsed = JSON.parse(rec.wh_comm_vehicle_types || "[]");
                return Array.isArray(parsed) ? parsed : [];
              } catch {
                return [];
              }
            })(),
        wh_skills: skillsArray, // ✅ always an array
      });

      // Prefill ongoing status flags
      setStillWorking(rec.wh_type === "Working" && !rec.wh_dateto);
      setStillUnemployed(rec.wh_type === "Unemployed" && !rec.wh_dateto);
      setstillSchool(rec.wh_type === "School" && !rec.wh_dateto);
      setStillDrivingSchool(rec.wh_type === "Driving School" && !rec.wh_dateto);
      setStillMilitary(rec.wh_type === "Military" && !rec.wh_dateto);
    } else {
      // Adding a new record → reset everything
      setEditRec(null);
      setForm({
        wh_type: "Working",
        wh_name: "",
        wh_confname: "",
        wh_conlname: "",
        wh_contact_position: "",
        wh_address1: "",
        wh_address2: "",
        wh_country: "",
        wh_postcode: "",
        wh_city: "",
        wh_province: "",
        wh_phone: "",
        wh_email: "",
        wh_datefrom: dayjs().format("YYYY-MM-DD"),
        wh_dateto: dayjs().format("YYYY-MM-DD"),
        wh_reason: "",
        wh_position: "",
        wh_status: "No",
        wh_terminated: "",
        wh_comm_vehicle: "",
        wh_comm_vehicle_types: [],
      });

      // Reset all "still..." states
      setStillWorking(false);
      setStillUnemployed(false);
      setstillSchool(false);
      setStillDrivingSchool(false);
      setStillMilitary(false);
    }

    setShowDrawer(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "wh_comm_vehicle" && value !== "Yes"
        ? { wh_comm_vehicle_types: [] }
        : {}),
    }));
  };

  const handlePendingFileSelection = (e) => {
    const nextFiles = Array.from(e.target.files || []);
    e.target.value = "";

    if (!nextFiles.length) return;

    const invalidFile = nextFiles.find(
      (file) => !allowedAttachmentTypes.includes(file.type),
    );
    if (invalidFile) {
      alert("Only PDF, JPG, or PNG files are allowed.");
      return;
    }

    setPendingFiles((prev) => [...prev, ...nextFiles]);
  };

  const handleRemovePendingFile = (fileToRemove) => {
    setPendingFiles((prev) =>
      prev.filter(
        (file) =>
          !(
            file.name === fileToRemove.name &&
            file.size === fileToRemove.size &&
            file.lastModified === fileToRemove.lastModified
          ),
      ),
    );
  };

  const uploadAttachments = async () => {
    if (!pendingFiles.length) return;

    try {
      setIsSavingAttachments(true);

      const formData = new FormData();
      formData.append("userId", userId);
      formData.append("taskKey", EMPLOYMENT_HISTORY_TASK_KEY);
      pendingFiles.forEach((file) => formData.append("files", file));

      await axios.post(
        `${BASE_URL}/api/custom-tasks/upload-attachment?taskKey=${encodeURIComponent(
          EMPLOYMENT_HISTORY_TASK_KEY,
        )}&userId=${userId}`,
        formData,
        {
          headers: {
            ...authHeader,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      setPendingFiles([]);
      await fetchAttachments();
      alert("Attachments uploaded successfully.");
    } catch (err) {
      console.error("Failed to upload work history attachments", err);
      alert(err.response?.data?.message || "Failed to upload attachments.");
    } finally {
      setIsSavingAttachments(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();

    const fromDate = new Date(form.wh_datefrom);
    const toDate = new Date(form.wh_dateto);

    const isOngoing =
      (form.wh_type === "Working" && stillWorking) ||
      (form.wh_type === "Unemployed" && stillUnemployed) ||
      (form.wh_type === "School" && stillSchool) ||
      (form.wh_type === "Driving School" && stillDrivingSchool) ||
      (form.wh_type === "Military" && stillMilitary);

    if (!isOngoing && fromDate > toDate) {
      alert("From Date must be earlier than or equal to To Date.");
      return;
    }

    let status = "No";
    let endDate = form.wh_dateto;

    if (form.wh_type === "Working" && stillWorking) {
      status = "Yes";
      endDate = null;
    }
    if (form.wh_type === "Unemployed" && stillUnemployed) {
      status = "Yes";
      endDate = null;
    }
    if (form.wh_type === "School" && stillSchool) {
      status = "Yes";
      endDate = null;
    }
    if (form.wh_type === "Driving School" && stillDrivingSchool) {
      status = "Yes";
      endDate = null;
    }
    if (form.wh_type === "Military" && stillMilitary) {
      status = "Yes";
      endDate = null;
    }

    const payload = {
      ...form,
      wh_userid: userId,
      wh_status: status,
      wh_dateto: endDate,
      wh_comm_vehicle_types:
        form.wh_comm_vehicle === "Yes" ? form.wh_comm_vehicle_types || [] : [],
    };

    try {
      if (editRec) {
        await axios.put(
          `${BASE_URL}/api/work-history/${editRec.wh_id}`,
          payload,
          { headers: authHeader },
        );
      } else {
        await axios.post(`${BASE_URL}/api/work-history`, payload, {
          headers: authHeader,
        });
      }

      setShowDrawer(false);
      fetchData();
      alert(editRec ? "Updated successfully" : "Created successfully");
    } catch (err) {
      console.error("Save error", err);
      alert(err.response?.data?.message || "Failed to save.");
    }
  };

  const handleDelete = async (wh_id) => {
    // if (isReadOnly) return;
    if (!window.confirm("Are you sure you want to delete this entry?")) return;

    try {
      await axios.delete(`${BASE_URL}/api/work-history/${wh_id}`, {
        headers: authHeader,
      });
      fetchData(); // Refresh the list after deletion
    } catch (err) {
      console.error("Delete error", err);
      alert("Failed to delete entry.");
    }
  };

  const handleDeleteSavedAttachment = async (attachment) => {
    const confirmed = window.confirm(
      `Delete uploaded file "${attachment.ta_original_name || "attachment"}"?`,
    );
    if (!confirmed) return;

    try {
      setIsDeletingAttachmentId(attachment.ta_id);
      await axios.delete(
        `${BASE_URL}/api/custom-tasks/attachments/${attachment.ta_id}`,
        {
          headers: authHeader,
        },
      );
      setExistingAttachments((prev) =>
        prev.filter((file) => file.ta_id !== attachment.ta_id),
      );
      alert("Attachment deleted successfully.");
    } catch (err) {
      console.error("Failed to delete work history attachment", err);
      alert(err.response?.data?.message || "Failed to delete attachment.");
    } finally {
      setIsDeletingAttachmentId(null);
    }
  };

  return (
    <>
      {/* 🔝 Header Section with Status + Cancel Button */}
      <div className="d-flex justify-content-between align-items-center pt-3">
        <div className="d-flex align-items-center">
          <span className="me-2 text-danger"></span>
          <span style={{ fontSize: "18px", fontWeight: "500" }}>
            Pg. 2 - Work and School History
          </span>
        </div>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => navigate(-1)}
        >
          Return
        </button>
      </div>

      <Container className="form-wrapper my-4">
        <div className="row align-items-center mb-4">
          <div className="col-3 text-start">
            <img src={logo} alt="Company Logo" style={{ width: "150px" }} />
          </div>
          <div className="col-6 text-center">
            <h2 className="m-0">Pg. 2 - Work and School History</h2>
          </div>
          <CompanyAddress />
        </div>

        <Card className="mb-4">
          <Card.Body>
            <Card.Title className="text-center">
              Your Employment History
            </Card.Title>
            <Button
              variant="outline-primary"
              onClick={() => openDrawer(null)}
              disabled={!isAdmin && isSigned}
            >
              Add History
            </Button>

            {records.length === 0 ? (
              <p className="mt-3 text-muted fst-italic">
                No work history entries yet.
              </p>
            ) : (
              <ListGroup className="mt-3">
                {records.map((rec) => (
                  <ListGroup.Item className="work-history-item" key={rec.wh_id}>
                    <div className="work-history-main">
                      <div className="work-history-company">
                        <strong>Type: </strong>
                        {rec.wh_type}
                      </div>
                      <div className="work-history-company">
                        <strong>Name: </strong>
                        {rec.wh_name || "-"}
                      </div>
                      <div className="work-history-dates">
                        <strong>From: </strong>
                        {dayjs(rec.wh_datefrom).format("YYYY-MM")}{" "}
                        <strong>To: </strong>
                        {rec.wh_dateto
                          ? dayjs(rec.wh_dateto).format("YYYY-MM")
                          : "Present"}
                      </div>
                      {isAdmin && rec.wh_type === "Working" && (
                        <div className="work-history-reference-status">
                          <strong>Reference Check: </strong>
                          {getReferenceMeta(rec).statusText}
                        </div>
                      )}
                    </div>

                    <div className="work-history-actions">
                      {isAdmin && rec.wh_type === "Working" && (
                        <Dropdown align="end">
                          <Dropdown.Toggle
                            variant="outline-primary"
                            size="sm"
                            className="reference-action-btn"
                          >
                            Reference Check
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item
                              onClick={() => handleSendReferenceCheck(rec)}
                            >
                              Send
                            </Dropdown.Item>
                            <Dropdown.Item
                              onClick={() =>
                                navigate(
                                  `/reference-check-results/${getReferenceMeta(rec).status.rc_id}`,
                                )
                              }
                              disabled={!getReferenceMeta(rec).canView}
                            >
                              View
                            </Dropdown.Item>
                            <Dropdown.Item
                              onClick={() =>
                                handleCancelReferenceCheck(
                                  getReferenceMeta(rec).status?.rc_id,
                                )
                              }
                              disabled={
                                getReferenceMeta(rec).status?.rc_status !==
                                "pending"
                              }
                            >
                              Cancel
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      )}
                      <Button
                        variant="light"
                        size="sm"
                        onClick={() => openDrawer(rec)}
                        disabled={!isAdmin && isSigned}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDelete(rec.wh_id)}
                        disabled={!isAdmin && isSigned}
                      >
                        Delete
                      </Button>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </Card.Body>
        </Card>

        <Offcanvas
          show={showDrawer}
          onHide={() => setShowDrawer(false)}
          placement="start"
          scroll
          backdrop
          className="offcanvas-wide"
        >
          <Offcanvas.Header closeButton>
            <Offcanvas.Title>
              {editRec ? "Edit Previous Employment" : "Add Previous Employment"}
            </Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body>
            <Form className="form-box" onSubmit={handleSave}>
              {/* Entry Type */}
              <Form.Group className="mb-4">
                <Form.Label>
                  <strong>Entry Type</strong>
                </Form.Label>
                <Form.Select
                  name="wh_type"
                  value={form.wh_type}
                  onChange={handleChange}
                >
                  <option value="Working">Working</option>
                  <option value="Unemployed">Unemployed</option>
                  <option value="School">School</option>
                  <option value="Driving School">Driving School</option>
                  <option value="Military">Military</option>
                </Form.Select>
              </Form.Group>

              {/* WORKING SECTION */}
              {form.wh_type === "Working" && (
                <>
                  {/* 🔹 Position Details */}
                  <h6 className="section-title">Position Details</h6>
                  <Row className="mb-3">
                    <Col md={6}>
                      <label>
                        What was your position?
                      </label>
                      <input
                        className="form-control"
                        name="wh_position"
                        value={form.wh_position}
                        onChange={handleChange}
                      />
                    </Col>
                    <Col md={6}>
                      <label>
                        Did you operate a commercial vehicle?{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        name="wh_comm_vehicle"
                        value={form.wh_comm_vehicle || ""}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Please select</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </select>
                    </Col>
                    {form.wh_comm_vehicle === "Yes" && (
                      <Col md={6} className="mt-3">
                        <label>
                          What type of commercial vehicle?{" "}
                          <span className="text-danger">*</span>
                        </label>
                        <Select
                          isMulti
                          options={equipOptions.map((opt) => ({
                            value: opt.eo_name,
                            label: opt.eo_name,
                          }))}
                          value={(form.wh_comm_vehicle_types || []).map((val) => ({
                            value: val,
                            label: val,
                          }))}
                          onChange={(selected) =>
                            setForm((prev) => ({
                              ...prev,
                              wh_comm_vehicle_types: (selected || []).map(
                                (item) => item.value,
                              ),
                            }))
                          }
                        />
                      </Col>
                    )}
                  </Row>

                  {/* 🔹 Company Information */}
                  <h6 className="section-title">Company Information</h6>
                  <Row className="mb-3">
                    <Col md={6}>
                      <label>
                        Company Name <span className="text-danger">*</span>
                      </label>
                      <input
                        className="form-control"
                        name="wh_name"
                        value={form.wh_name}
                        onChange={handleChange}
                        required
                      />
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={6}>
                      <label>Address 1</label>
                      <input
                        className="form-control"
                        name="wh_address1"
                        value={form.wh_address1}
                        onChange={handleChange}
                      />
                    </Col>
                    <Col md={6}>
                      <label>Address 2</label>
                      <input
                        className="form-control"
                        name="wh_address2"
                        value={form.wh_address2}
                        onChange={handleChange}
                      />
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={3}>
                      <label>City</label>
                      <input
                        className="form-control"
                        name="wh_city"
                        value={form.wh_city}
                        onChange={handleChange}
                      />
                    </Col>
                    <Col md={3}>
                      <label>Province</label>
                      <input
                        className="form-control"
                        name="wh_province"
                        value={form.wh_province}
                        onChange={handleChange}
                      />
                    </Col>
                    <Col md={3}>
                      <label>
                        Country <span className="text-danger">*</span>
                      </label>
                      <input
                        className="form-control"
                        name="wh_country"
                        value={form.wh_country}
                        onChange={handleChange}
                        required
                      />
                    </Col>
                    <Col md={3}>
                      <label>Postal Code</label>
                      <input
                        className="form-control"
                        name="wh_postcode"
                        value={form.wh_postcode}
                        onChange={handleChange}
                      />
                    </Col>
                  </Row>

                  {/* 🔹 Company Contact */}
                  <h6 className="section-title">Company Contact</h6>
                  <Row className="mb-3">
                    <Col md={6}>
                      <label>
                        Contact's First Name{" "}
                        <span className="text-danger"> *</span>
                      </label>
                      <input
                        className="form-control"
                        name="wh_confname"
                        value={form.wh_confname}
                        onChange={handleChange}
                        required
                      />
                    </Col>
                    <Col md={6}>
                      <label>Contact's Last Name</label>
                      <input
                        className="form-control"
                        name="wh_conlname"
                        value={form.wh_conlname}
                        onChange={handleChange}
                      />
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={6}>
                      <label>
                        Company Phone<span className="text-danger"> *</span>
                      </label>
                      <input
                        type="tel"
                        className="form-control"
                        name="wh_phone"
                        value={form.wh_phone}
                        onChange={handleChange}
                        placeholder="780-555-1234"
                        pattern="^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$"
                        title="Use format like 780-555-1234 or (780) 555-1234"
                        required
                      />
                    </Col>
                    <Col md={6}>
                      <label>
                        Company Email<span className="text-danger"> *</span>
                      </label>
                      <input
                        className="form-control"
                        name="wh_email"
                        value={form.wh_email}
                        onChange={handleChange}
                        required
                      />
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={6}>
                      <label>Contact Position</label>
                      <input
                        className="form-control"
                        name="wh_contact_position"
                        value={form.wh_contact_position || ""}
                        onChange={handleChange}
                      />
                    </Col>
                  </Row>

                  {/* 🔹 Employment Dates */}
                  <h6 className="section-title">Employment Dates</h6>
                  <Row className="mb-2">
                    <Col md={6}>
                      <label>
                        From <span className="text-danger">*</span>
                      </label>
                      <input
                        type="date"
                        name="wh_datefrom"
                        className="form-control"
                        value={form.wh_datefrom}
                        onChange={handleChange}
                        required
                      />
                    </Col>
                    <Col md={6}>
                      <label>
                        To <span className="text-danger">*</span>
                      </label>
                      <input
                        type="date"
                        name="wh_dateto"
                        className="form-control"
                        value={form.wh_dateto}
                        onChange={handleChange}
                        disabled={stillWorking}
                        required
                      />
                    </Col>
                  </Row>
                  <div className="form-check mb-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="stillWorkingCheck"
                      checked={stillWorking}
                      onChange={(e) => setStillWorking(e.target.checked)}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="stillWorkingCheck"
                    >
                      Still Working Here
                    </label>
                  </div>

                  {/* 🔹 Reason For Leaving */}
                  <h6 className="section-title">Reason For Leaving</h6>
                  <Row className="mb-3">
                    <Col md={4}>
                      <label>
                        Terminated<span className="text-danger"> *</span>
                      </label>
                      <select
                        className="form-select"
                        name="wh_terminated"
                        value={form.wh_terminated || ""}
                        onChange={handleChange}
                        required
                      >
                        <option value="" disabled>
                          Please select
                        </option>
                        <option value="No">No</option>
                        <option value="Yes">Yes</option>
                      </select>
                    </Col>
                    <Col md={8}>
                      <label>Reason For Leaving</label>
                      <input
                        className="form-control"
                        name="wh_reason"
                        value={form.wh_reason}
                        onChange={handleChange}
                      />
                    </Col>
                  </Row>
                </>
              )}

              {/* UNEMPLOYED SECTION */}
              {form.wh_type === "Unemployed" && (
                <>
                  <h6 className="section-title">Unemployment Period</h6>
                  <Row className="mb-3">
                    <Col md={6}>
                      <label>
                        From <span className="text-danger">*</span>
                      </label>
                      <input
                        type="date"
                        name="wh_datefrom"
                        className="form-control"
                        value={form.wh_datefrom}
                        onChange={handleChange}
                        required
                      />
                    </Col>
                    <Col md={6}>
                      <label>
                        To <span className="text-danger">*</span>
                      </label>
                      <input
                        type="date"
                        name="wh_dateto"
                        className="form-control"
                        value={form.wh_dateto}
                        onChange={handleChange}
                        disabled={stillUnemployed}
                        required
                      />
                    </Col>
                  </Row>
                  <div className="form-check mb-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="stillUnemployedCheck"
                      checked={stillUnemployed}
                      onChange={(e) => setStillUnemployed(e.target.checked)}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="stillUnemployedCheck"
                    >
                      Or still unemployed
                    </label>
                  </div>
                  <Form.Group className="mb-3">
                    <Form.Label>Reason for Unemployment</Form.Label>
                    <Form.Control
                      name="wh_reason"
                      value={form.wh_reason}
                      onChange={handleChange}
                      as="textarea"
                      rows={2}
                    />
                  </Form.Group>
                </>
              )}

              {/* SCHOOL SECTION */}
              {form.wh_type === "School" && (
                <>
                  <h6 className="section-title">School Details</h6>
                  <Row className="mb-3">
                    <Col md={6}>
                      <label>
                        School Name <span className="text-danger">*</span>
                      </label>
                      <input
                        className="form-control"
                        name="wh_name"
                        value={form.wh_name}
                        onChange={handleChange}
                      />
                    </Col>
                    <Col md={6}>
                      <label>
                        Level of Education{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        name="wh_position"
                        value={form.wh_position}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select Level</option>
                        {form.wh_position &&
                          !eduOptions.some(
                            (edu) => edu.he_name === form.wh_position,
                          ) && (
                            <option value={form.wh_position}>
                              {form.wh_position}
                            </option>
                          )}
                        {eduOptions.map((edu) => (
                          <option key={edu.he_id} value={edu.he_name}>
                            {edu.he_name}
                          </option>
                        ))}
                      </select>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={6}>
                      <label>
                        From <span className="text-danger">*</span>
                      </label>
                      <input
                        type="date"
                        name="wh_datefrom"
                        className="form-control"
                        value={form.wh_datefrom}
                        onChange={handleChange}
                        required
                      />
                    </Col>
                    <Col md={6}>
                      <label>
                        To <span className="text-danger">*</span>
                      </label>
                      <input
                        type="date"
                        name="wh_dateto"
                        className="form-control"
                        value={form.wh_dateto}
                        onChange={handleChange}
                        disabled={stillSchool}
                        required
                      />
                    </Col>
                  </Row>
                  <div className="form-check mb-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="stillSchoolCheck"
                      checked={stillSchool}
                      onChange={(e) => setstillSchool(e.target.checked)}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="stillSchoolCheck"
                    >
                      Or still in school
                    </label>
                  </div>
                </>
              )}

              {form.wh_type === "Driving School" && (
                <>
                  <h6 className="section-title">Driving School</h6>
                  <Row className="mb-3">
                    <Col md={6}>
                      <label>
                        School Name <span className="text-danger">*</span>
                      </label>
                      <input
                        className="form-control"
                        name="wh_name"
                        value={form.wh_name}
                        onChange={handleChange}
                        required
                      />
                    </Col>
                    <Col md={6}>
                      <label>Phone</label>
                      <input
                        className="form-control"
                        name="wh_phone"
                        value={form.wh_phone}
                        onChange={handleChange}
                      />
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={3}>
                      <label>City</label>
                      <input
                        className="form-control"
                        name="wh_city"
                        value={form.wh_city}
                        onChange={handleChange}
                      />
                    </Col>
                    <Col md={3}>
                      <label>Province</label>
                      <input
                        className="form-control"
                        name="wh_province"
                        value={form.wh_province}
                        onChange={handleChange}
                      />
                    </Col>
                    <Col md={3}>
                      <label>
                        Country <span className="text-danger">*</span>
                      </label>
                      <input
                        className="form-control"
                        name="wh_country"
                        value={form.wh_country}
                        onChange={handleChange}
                        required
                      />
                    </Col>
                    <Col md={3}>
                      <label>Postal Code</label>
                      <input
                        className="form-control"
                        name="wh_postcode"
                        value={form.wh_postcode}
                        onChange={handleChange}
                      />
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6}>
                      <label>
                        From <span className="text-danger">*</span>
                      </label>
                      <input
                        type="date"
                        name="wh_datefrom"
                        className="form-control"
                        value={form.wh_datefrom}
                        onChange={handleChange}
                        required
                      />
                    </Col>
                    <Col md={6}>
                      <label>
                        To <span className="text-danger">*</span>
                      </label>
                      <input
                        type="date"
                        name="wh_dateto"
                        className="form-control"
                        value={form.wh_dateto}
                        onChange={handleChange}
                        disabled={stillDrivingSchool}
                        required
                      />
                    </Col>
                  </Row>
                  <div className="form-check mb-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="stillDrivingSchoolCheck"
                      checked={stillDrivingSchool}
                      onChange={(e) => setStillDrivingSchool(e.target.checked)}
                    />
                    <label className="form-check-label">
                      Or still in driving school
                    </label>
                  </div>

                  <Row className="mb-3">
                    <Col md={6}>
                      <label>
                        Did you graduate? <span className="text-danger">*</span>
                      </label>
                      <Form.Select
                        name="wh_graduated"
                        value={form.wh_graduated || ""}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </Form.Select>
                    </Col>
                    <Col md={6}>
                      <label>GPA</label>
                      <input
                        className="form-control"
                        name="wh_gpa"
                        value={form.wh_gpa || ""}
                        onChange={handleChange}
                      />
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6}>
                      <label>
                        Hours of Instruction{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <Form.Select
                        name="wh_hours_of_instruction"
                        value={form.wh_hours_of_instruction || ""}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select</option>
                        <option value="50">50</option>
                        <option value="80">80</option>
                        <option value="100">100</option>
                        <option value="200">200</option>
                      </Form.Select>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6}>
                      <label>
                        Safety Sensitive Functions (DOT)?{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <Form.Select
                        name="wh_safety_sensitive"
                        value={form.wh_safety_sensitive || ""}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </Form.Select>
                    </Col>
                    <Col md={6}>
                      <label>
                        Transport Canada / FMCSR?{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <Form.Select
                        name="wh_transport_regulation"
                        value={form.wh_transport_regulation || ""}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </Form.Select>
                    </Col>
                  </Row>

                  <h6 className="section-title">Skills Trained</h6>
                  {[
                    "Border Crossing",
                    "Log Books",
                    "Federal Motor Carrier Regulations",
                    "Hazardous Materials",
                  ].map((skill) => (
                    <Form.Check
                      key={skill}
                      type="checkbox"
                      label={skill}
                      checked={form.wh_skills?.includes(skill)}
                      onChange={(e) => {
                        const skills = form.wh_skills || [];
                        if (e.target.checked) {
                          setForm({ ...form, wh_skills: [...skills, skill] });
                        } else {
                          setForm({
                            ...form,
                            wh_skills: skills.filter((s) => s !== skill),
                          });
                        }
                      }}
                    />
                  ))}
                </>
              )}

              {form.wh_type === "Military" && (
                <>
                  <h6 className="section-title">Military Service</h6>

                  <Row className="mb-3">
                    <Col md={6}>
                      <label>
                        Start Date <span className="text-danger">*</span>
                      </label>
                      <input
                        type="date"
                        name="wh_datefrom"
                        className="form-control"
                        value={form.wh_datefrom}
                        onChange={handleChange}
                        required
                      />
                    </Col>
                    <Col md={6}>
                      <label>
                        End Date <span className="text-danger">*</span>
                      </label>
                      <input
                        type="date"
                        name="wh_dateto"
                        className="form-control"
                        value={form.wh_dateto}
                        onChange={handleChange}
                        disabled={stillMilitary}
                        required
                      />
                    </Col>
                  </Row>

                  <div className="form-check mb-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="stillMilitaryCheck"
                      checked={stillMilitary}
                      onChange={(e) => setStillMilitary(e.target.checked)}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="stillMilitaryCheck"
                    >
                      Or still in active service
                    </label>
                  </div>

                  <Row className="mb-3">
                    <Col md={6}>
                      <label>
                        Branch of Service <span className="text-danger">*</span>
                      </label>
                      <Form.Select
                        name="wh_branch_service"
                        value={form.wh_branch_service || ""}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select Branch</option>
                        <option value="Army">Army</option>
                        <option value="Air Force">Air Force</option>
                        <option value="Navy">Navy</option>
                        <option value="Marines">Marines</option>
                        <option value="Coast Guard">Coast Guard</option>
                      </Form.Select>
                    </Col>
                    <Col md={6}>
                      <label>Military Rank</label>
                      <input
                        className="form-control"
                        name="wh_military_rank"
                        value={form.wh_military_rank || ""}
                        onChange={handleChange}
                      />
                    </Col>
                  </Row>
                </>
              )}

              <div className="d-flex justify-content-between">
                <Button
                  variant="secondary"
                  onClick={() => setShowDrawer(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" variant="primary" disabled={isSavingAttachments}>
                  {isSavingAttachments ? "Saving..." : "Save"}
                </Button>
              </div>
            </Form>
          </Offcanvas.Body>
        </Offcanvas>

        <hr className="my-4" />

        <h5>Attachments</h5>

        <form onSubmit={(e) => e.preventDefault()}>
          <div className="mb-3">
            <label>Select File (PDF / JPG / PNG)</label>
            <input
              type="file"
              className="form-control"
              accept=".pdf,.jpg,.jpeg,.png"
              multiple
              disabled={(!isAdmin && isSigned) || isSavingAttachments}
              onChange={handlePendingFileSelection}
            />
          </div>

          {pendingFiles.length > 0 && (
            <div className="mt-4 p-3 bg-light rounded border">
              <div className="text-muted mb-2">Files Ready To Save</div>
              <ul className="list-unstyled mb-0">
                {pendingFiles.map((file, idx) => (
                  <li
                    key={`${file.name}_${file.size}_${file.lastModified}_${idx}`}
                    className="d-flex justify-content-between align-items-center py-1"
                  >
                    <span>{file.name}</span>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleRemovePendingFile(file)}
                      disabled={(!isAdmin && isSigned) || isSavingAttachments}
                      title="Delete"
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </form>

        <UploadedAttachmentList
          title="Uploaded Files"
          files={existingAttachments}
          getFileKey={(file) => file.ta_id}
          getFileName={(file) => file.ta_original_name || "Attachment"}
          getFileViewUrl={(file) =>
            `${BASE_URL}/api/custom-tasks/attachment-file/${file.ta_id}?mode=view`
          }
          getFileDownloadUrl={(file) =>
            `${BASE_URL}/api/custom-tasks/attachment-file/${file.ta_id}?mode=download`
          }
          getFileMeta={(file) =>
            file.ta_uploaded_at
              ? `Uploaded ${dayjs(file.ta_uploaded_at).format("YYYY-MM-DD HH:mm")}`
              : null
          }
          canDelete={Boolean(isAdmin || !isSigned)}
          onDeleteFile={handleDeleteSavedAttachment}
          deletingFileId={isDeletingAttachmentId}
        />

        <div className="d-flex justify-content-end mt-3">
          <button
            type="button"
            className="btn btn-primary"
            onClick={uploadAttachments}
            disabled={
              !pendingFiles.length ||
              (!isAdmin && isSigned) ||
              isSavingAttachments
            }
          >
            {isSavingAttachments ? "Saving..." : "Save"}
          </button>
        </div>

        <hr className="my-5" />

        {userId && (
          <>
            <SignatureManager
              taskName="employment_history"
              taskId={19}
              userId={isAdmin && paramId ? paramId : userId}
              isAdmin={isAdmin}
            />
            <ReviewManager
              taskName="employment_history"
              taskId={19}
              userId={isAdmin && paramId ? paramId : userId}
              isAdmin={isAdmin}
            />
          </>
        )}
      </Container>
    </>
  );
}

export default WorkHistoryPage;
